import { decodeDisplayText } from './displayText';
import { t as lnT } from '../i18n';
import {useTranslation} from 'react-i18next';
import React from 'react';
import styled, { css } from 'styled-components';
import { useQuery } from '@apollo/client';
import { DEFAULT_HIGHLIGHT_COLOR, HIGHLIGHT_QUOTE_THEME } from './highlight-colors';
import { GET_MESSAGE_REPLY_PREVIEW } from '../gql-operations/queries/message/get-message-reply-preview';
import PlayIconOverlay from '../components/Messages/Sections/PlayIconOverlay';
import { createMarkdownMediaOptions, InlineVideoLink, isDirectMarkdownVideo } from './inlineMarkdownVideo';

export const COMPACT_MARKDOWN_TOKENS = {
    body: {
        fontSize: 14,
        lineHeight: 1.4,
        paragraphMarginBottom: 8
    },
    headings: {
        h1: { fontSize: 20, lineHeight: 1.3, fontWeight: 700, margin: '16px 0 8px' },
        h2: { fontSize: 18, lineHeight: 1.35, fontWeight: 700, margin: '14px 0 8px' },
        h3: { fontSize: 16, lineHeight: 1.35, fontWeight: 600, margin: '12px 0 6px' },
        h4: { fontSize: 15, lineHeight: 1.4, fontWeight: 600, margin: '10px 0 6px' },
        h5: { fontSize: 14, lineHeight: 1.4, fontWeight: 600, margin: '8px 0 4px' },
        h6: { fontSize: 13, lineHeight: 1.4, fontWeight: 600, margin: '8px 0 4px' }
    },
    list: {
        margin: '4px 0',
        paddingLeft: '16px',
        itemMargin: '2px 0'
    },
    quote: {
        borderWidth: 3,
        paddingLeft: 8,
        margin: '4px 0'
    },
    inlineCode: {
        padding: '2px 4px',
        borderRadius: 3,
        fontFamily: 'monospace'
    },
    codeBlock: {
        padding: 8,
        borderRadius: 6
    },
    hr: {
        margin: '12px 0'
    },
    table: {
        wrapperMargin: '12px 0',
        minWidth: 640,
        columnWidth: 160,
        cellPadding: '10px 12px',
        borderRadius: 8
    }
};

// Highlight-quote tint palette (kept in sync with the extension's save-time
// palette names in Highlight.color). Unknown/missing color falls back to yellow.
export const HIGHLIGHT_COLOR_PALETTE = HIGHLIGHT_QUOTE_THEME;

export const getHighlightColorTheme = color =>
    HIGHLIGHT_QUOTE_THEME[color] || HIGHLIGHT_QUOTE_THEME[DEFAULT_HIGHLIGHT_COLOR];

const SURFACE_PRESETS = {
    light: {
        textColor: 'rgba(0, 0, 0, 0.8)',
        headingColor: 'rgba(0, 0, 0, 0.9)',
        mutedHeadingColor: 'rgba(0, 0, 0, 0.75)',
        quoteColor: 'rgba(0, 0, 0, 0.7)',
        codeBackground: '#f5f5f5',
        preBackground: '#f5f5f5',
        hrColor: 'rgba(0, 0, 0, 0.2)',
        linkColor: '#1f6fd6'
    },
    dark: {
        textColor: 'rgba(255, 255, 255, 0.95)',
        headingColor: '#ffffff',
        mutedHeadingColor: 'rgba(255, 255, 255, 0.75)',
        quoteColor: 'rgba(255, 255, 255, 0.7)',
        codeBackground: 'rgba(255, 255, 255, 0.1)',
        preBackground: 'rgba(0, 0, 0, 0.3)',
        hrColor: 'rgba(255, 255, 255, 0.35)',
        linkColor: '#5ca2ff'
    }
};

const getSurfaceColors = ({ surface = 'light', linkColor, textColor, quoteColor } = {}) => {
    const preset = SURFACE_PRESETS[surface] || SURFACE_PRESETS.light;
    const resolvedLinkColor = linkColor || preset.linkColor;
    const resolvedTextColor = textColor || preset.textColor;
    const resolvedQuoteColor = quoteColor || preset.quoteColor;

    return {
        textColor: resolvedTextColor,
        headingColor: preset.headingColor,
        mutedHeadingColor: preset.mutedHeadingColor,
        quoteColor: resolvedQuoteColor,
        quoteBorderColor: resolvedLinkColor,
        codeBackground: preset.codeBackground,
        preBackground: preset.preBackground,
        hrColor: preset.hrColor,
        linkColor: resolvedLinkColor
    };
};

const IMAGE_LINK_LABEL_REGEX = /^image\s+\d+\s*:\s*image$/i;
const MEDIA_LIKE_URL_REGEX = /(?:\.(?:png|jpe?g|gif|webp|avif|svg))(?:[?#].*)?$|\/media\/\d+(?:[/?#].*)?$/i;
const VIDEO_LINK_LABEL_REGEX = /^video(?:\s+\d+)?(?:\s*:\s*video)?(?:\s+file)?$/i;
const VIDEO_LIKE_URL_REGEX = /(?:\.(?:mp4|m4v|mov|webm|ogv|m3u8))(?:[?#].*)?$|\/(?:video|media)\/[^/?#]+(?:[/?#].*)?$/i;

const isImageLikeHref = href => typeof href === 'string' && MEDIA_LIKE_URL_REGEX.test(href);
const isVideoLikeHref = href => typeof href === 'string' && VIDEO_LIKE_URL_REGEX.test(href);

// AI chat answers about saved messages use this custom scheme so an item
// like "which messages mention X" can be a clickable in-app preview instead
// of plain "Item 1/2/3" text. See ai-chat-completion-stream.ts (server) for
// where this is emitted.
const MESSAGE_REF_HREF_PREFIX = 'luckynote-message:';

const isMessageRefHref = href => typeof href === 'string' && href.startsWith(MESSAGE_REF_HREF_PREFIX);

const extractMessageRefId = href => String(href || '').slice(MESSAGE_REF_HREF_PREFIX.length).trim();

// A tinted-blue fill (the original design) can wash out to near-illegible
// against whatever busy/dark background sits behind a chat bubble — pick a
// neutral, theme-aware fill/border/text combo instead of depending on
// linkColor for both text and background at once.
const MESSAGE_REF_CHIP_THEME = {
    light: {
        background: 'rgba(0, 0, 0, 0.06)',
        hoverBackground: 'rgba(0, 0, 0, 0.1)',
        border: '1px solid rgba(0, 0, 0, 0.14)',
        color: 'rgba(0, 0, 0, 0.82)'
    },
    dark: {
        background: 'rgba(255, 255, 255, 0.14)',
        hoverBackground: 'rgba(255, 255, 255, 0.22)',
        border: '1px solid rgba(255, 255, 255, 0.22)',
        color: 'rgba(255, 255, 255, 0.95)'
    }
};

const MessageReferenceIcon = ({ color }) => (
    <svg width="11" height="11" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0 }}>
        <path
            d="M4 2.5h5.5L13 6v7.5a1 1 0 01-1 1H4a1 1 0 01-1-1v-10a1 1 0 011-1z"
            stroke={color}
            strokeWidth="1.3"
            strokeLinejoin="round"
        />
        <path d="M9 2.5V6h4" stroke={color} strokeWidth="1.3" strokeLinejoin="round" />
    </svg>
);

const InlineMessageReferenceLink = ({ id, label, surface = 'light', onMessageRefClick }) => {
    const {t: lnT} = useTranslation();

    const [isHovered, setIsHovered] = React.useState(false);
    const theme = MESSAGE_REF_CHIP_THEME[surface] || MESSAGE_REF_CHIP_THEME.light;

    const handleClick = event => {
        event.preventDefault();
        event.stopPropagation();
        if (typeof onMessageRefClick === 'function' && id) {
            onMessageRefClick(id);
        }
    };

    return (
        <a
            href="#"
            onClick={handleClick}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '5px',
                padding: '2px 9px',
                margin: '1px 2px',
                borderRadius: '12px',
                background: isHovered ? theme.hoverBackground : theme.background,
                border: theme.border,
                color: theme.color,
                textDecoration: 'none',
                fontWeight: 500,
                fontSize: '0.92em',
                lineHeight: 1.4,
                cursor: 'pointer',
                maxWidth: '100%',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                verticalAlign: 'baseline',
                transition: 'background 0.12s ease'
            }}
        >
            <MessageReferenceIcon color={theme.color} />
            {label || lnT('messages:openSavedMessage')}
        </a>
    );
};

// urlMeta is an array field on Message (see server/src/modules/graphql/type-defs/message.graphql)
// — always read the first entry, matching DefaultMessage.js's renderUrlMeta/getFirstUrlMeta pattern.
const getFirstUrlMeta = previewMessage =>
    (Array.isArray(previewMessage?.urlMeta) ? previewMessage.urlMeta[0] : previewMessage?.urlMeta) || null;

// For urlMeta saves, the message's own `title` field is frequently just the
// raw saved URL (e.g. "https://www.youtube.co...") rather than a human
// title — the fetched link-preview metadata (urlMeta.title / .description)
// is the actually-readable label, so it takes precedence whenever present.
// Non-urlMeta saves (text/image/file) keep the original title-first order.
const resolveMessageCardTitle = previewMessage => {
    const firstUrlMeta = getFirstUrlMeta(previewMessage);

    return (
        firstUrlMeta?.title ||
        firstUrlMeta?.description ||
        previewMessage?.title ||
        previewMessage?.body ||
        previewMessage?.file?.displayName ||
        previewMessage?.file?.name ||
        previewMessage?.image?.displayName ||
        previewMessage?.image?.name ||
        'Referenced message'
    )
        .toString()
        .replace(/\s+/g, ' ')
        .trim()
        .slice(0, 140);
};

const MESSAGE_CARD_THEME = {
    light: {
        background: 'rgba(0, 0, 0, 0.035)',
        hoverBackground: 'rgba(0, 0, 0, 0.06)',
        border: '1px solid rgba(0, 0, 0, 0.12)',
        titleColor: 'rgba(0, 0, 0, 0.85)',
        folderColor: 'rgba(0, 0, 0, 0.55)',
        skeletonColor: 'rgba(0, 0, 0, 0.08)'
    },
    dark: {
        background: 'rgba(255, 255, 255, 0.08)',
        hoverBackground: 'rgba(255, 255, 255, 0.14)',
        border: '1px solid rgba(255, 255, 255, 0.16)',
        titleColor: 'rgba(255, 255, 255, 0.95)',
        folderColor: 'rgba(255, 255, 255, 0.65)',
        skeletonColor: 'rgba(255, 255, 255, 0.12)'
    }
};

// Compact inline chip: small thumbnail + single-line truncated title only.
// No folder label, no publisher, no "saved 2x" — that context stays in the
// surrounding plain-text markdown the model writes. Sized to sit inline with
// text and wrap like a tag/word (see InlineMessageCard below).
const MESSAGE_CARD_THUMBNAIL_SIZE = 28;
const MESSAGE_CARD_CHIP_MAX_WIDTH = 220;

const isVideoLikeFileType = file =>
    /video/i.test(String(file?.type || file?.mime || ''));

const getFileBadge = file => {
    const value = String(file?.displayName || file?.name || '').toLowerCase();
    const mime = String(file?.type || file?.mime || '').toLowerCase();
    const extension = value.match(/\.([a-z0-9]{1,6})$/)?.[1] || '';

    if (mime.includes('pdf') || extension === 'pdf') return { label: lnT('messages:pdf'), color: '#e56b6f' };
    if (mime.includes('word') || ['doc', 'docx', 'rtf'].includes(extension)) return { label: lnT('messages:doc'), color: '#5b8def' };
    if (mime.includes('sheet') || mime.includes('excel') || ['xls', 'xlsx', 'csv'].includes(extension)) {
        return { label: lnT('messages:xls'), color: '#4caf79' };
    }
    if (mime.includes('presentation') || ['ppt', 'pptx', 'key'].includes(extension)) return { label: lnT('messages:ppt'), color: '#e08a4b' };
    if (mime.includes('zip') || mime.includes('archive') || ['zip', 'rar', '7z', 'tar', 'gz'].includes(extension)) {
        return { label: lnT('messages:zip'), color: '#a78bfa' };
    }
    if (mime.includes('audio') || ['mp3', 'wav', 'm4a', 'aac', 'flac'].includes(extension)) return { label: lnT('messages:aud'), color: '#d17cf2' };
    if (mime.includes('video') || ['mp4', 'mov', 'm4v', 'webm', 'avi'].includes(extension)) return { label: lnT('messages:vid'), color: '#f08a72' };
    if (mime.includes('image') || ['png', 'jpg', 'jpeg', 'gif', 'webp', 'heic'].includes(extension)) return { label: lnT('messages:img'), color: '#42b7c8' };
    return { label: extension ? extension.slice(0, 4).toUpperCase() : lnT('messages:file_9ef2faff'), color: '#8d99ae' };
};

// A VIDEO-type upload stores its poster frame the same way images do
// (file.thumbnail.{sm,md,lg}); DefaultMessage.js's FileSection renders a full
// <video> element with controls for these, which is too heavy for a compact
// card, so the mini card resolves just the poster image + play overlay
// instead — no inline playback, matches the urlMeta-video treatment.
const resolveVideoFileThumbnail = file =>
    file?.thumbnail?.lg || file?.thumbnail?.md || file?.thumbnail?.sm || null;

const InlineMessageFileBadge = ({ file, size }) => {
    const {t: lnT} = useTranslation();

    const badge = getFileBadge(file);
    return (
        <span
            style={{
                width: `${size}px`,
                height: `${size}px`,
                borderRadius: '10px',
                flexShrink: 0,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: `${badge.color}26`,
                border: `1px solid ${badge.color}70`,
                color: badge.color,
                fontSize: size <= 28 ? '8px' : '10px',
                fontWeight: 800,
                letterSpacing: '0.02em',
                boxSizing: 'border-box'
            }}
            title={file?.displayName || file?.name || lnT('messages:file')}
        >
            {badge.label}
        </span>
    );
};

// Renders a saved-message reference ([label](luckynote-message:<id>)) inline
// in an AI chat reply as a compact chip — small thumbnail + single-line
// title — instead of a plain link, so the referenced save is recognizable
// without an extra click. Falls back to the original chip link if the
// message can't be loaded (deleted, network error, invalid id) so a bad
// reference never breaks the chat.
// Compact thumbnail-only media for the inline reference chip. Reuses the
// same per-type rendering the full message view (DefaultMessage.js) uses,
// instead of hand-rolling a generic thumbnail:
// - image  -> compact inline <img>
// - file   -> a compact type-aware file badge when no thumbnail exists
// - urlMeta -> compact inline <img> when a preview image exists
// - text-only -> no icon box at all; the chip renders as a plain text label
const InlineMessageCardMedia = ({ previewMessage, size, theme }) => {
    if (previewMessage?.image) {
        const imageUrl = previewMessage.image.cdn_url || previewMessage.image.url || null;
        if (!imageUrl) {
            return null;
        }

        return (
            <span
                style={{
                    width: `${size}px`,
                    height: `${size}px`,
                    borderRadius: '10px',
                    flexShrink: 0,
                    overflow: 'hidden',
                    background: theme.skeletonColor
                }}
            >
                <img
                    src={imageUrl}
                    alt=""
                    style={{ width: `${size}px`, height: `${size}px`, objectFit: 'cover', display: 'block' }}
                />
            </span>
        );
    }

    if (previewMessage?.file) {
        const isVideoFile = isVideoLikeFileType(previewMessage.file);
        const videoThumbnail = isVideoFile ? resolveVideoFileThumbnail(previewMessage.file) : null;

        if (videoThumbnail) {
            return (
                <span style={{ position: 'relative', width: `${size}px`, height: `${size}px`, flexShrink: 0, display: 'block' }}>
                    <img
                        src={videoThumbnail}
                        alt=""
                        style={{
                            width: `${size}px`,
                            height: `${size}px`,
                            borderRadius: '10px',
                            objectFit: 'cover',
                            display: 'block',
                            background: theme.skeletonColor
                        }}
                    />
                    <PlayIconOverlay size={size} />
                </span>
            );
        }

        return <InlineMessageFileBadge file={previewMessage.file} size={size} />;
    }

    const firstUrlMeta = getFirstUrlMeta(previewMessage);
    if (firstUrlMeta) {
        const urlImage = firstUrlMeta.image_cdn_url || firstUrlMeta.image || firstUrlMeta.thumbnail?.sm || null;
        if (!urlImage) {
            return null;
        }

        return (
            <img
                src={urlImage}
                alt=""
                style={{
                    width: `${size}px`,
                    height: `${size}px`,
                    borderRadius: '10px',
                    objectFit: 'cover',
                    display: 'block'
                }}
            />
        );
    }

    // Text-only save: no icon box, just the title label handled by the caller.
    return null;
};

const InlineMessageCard = ({ id, label, surface = 'light', onMessageRefClick }) => {
    const {t: lnT} = useTranslation();

    const [isHovered, setIsHovered] = React.useState(false);
    const theme = MESSAGE_CARD_THEME[surface] || MESSAGE_CARD_THEME.light;
    const isValidId = /^[a-fA-F0-9]{24}$/.test(String(id || ''));

    const { data, loading, error } = useQuery(GET_MESSAGE_REPLY_PREVIEW, {
        variables: { data: { id } },
        skip: !isValidId,
        fetchPolicy: 'cache-first'
    });

    if (!isValidId) {
        return <InlineMessageReferenceLink id={id} label={label} surface={surface} onMessageRefClick={onMessageRefClick} />;
    }

    const previewMessage = data?.getMessage;

    // Deleted/missing/errored reference: never break the chat, fall back to
    // the original plain chip link (it will show a not-found notification on
    // click via the existing onMessageRefClick handler).
    if (!loading && (error || !previewMessage)) {
        return <InlineMessageReferenceLink id={id} label={label} surface={surface} onMessageRefClick={onMessageRefClick} />;
    }

    const title = previewMessage ? resolveMessageCardTitle(previewMessage) : label || lnT('messages:loadingSavedMessage');
    const firstUrlMeta = getFirstUrlMeta(previewMessage);
    const hasUrlPreviewImage = Boolean(firstUrlMeta?.image_cdn_url || firstUrlMeta?.image || firstUrlMeta?.thumbnail?.sm);
    const hasMedia = Boolean(previewMessage?.image?.url || previewMessage?.image?.cdn_url || previewMessage?.file || hasUrlPreviewImage);
    const fileThumbnail = previewMessage?.file?.thumbnail?.lg || previewMessage?.file?.thumbnail?.md || previewMessage?.file?.thumbnail?.sm;
    const fileNeedsRoom = Boolean(previewMessage?.file && !fileThumbnail);
    const referenceMediaSize = fileNeedsRoom ? 34 : MESSAGE_CARD_THUMBNAIL_SIZE;

    const handleClick = event => {
        event.preventDefault();
        event.stopPropagation();
        if (typeof onMessageRefClick === 'function' && id) {
            onMessageRefClick(id);
        }
    };

    return (
        <a
            href="#"
            onClick={handleClick}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                padding: hasMedia ? '2px 10px 2px 2px' : '5px 14px 5px 14px',
                margin: hasMedia ? '1px 2px' : '2px 4px',
                borderRadius: '14px',
                background: isHovered ? theme.hoverBackground : theme.background,
                border: theme.border,
                textDecoration: 'none',
                cursor: 'pointer',
                width: 'fit-content',
                maxWidth: `${fileNeedsRoom ? 320 : MESSAGE_CARD_CHIP_MAX_WIDTH}px`,
                // Floor height at the media chip's effective height (28px thumbnail + its
                // 2px top/bottom padding) so text-only chips don't render visibly shorter
                // than thumbnail chips when several appear together in a list.
                minHeight: `${referenceMediaSize + 4}px`,
                boxSizing: 'border-box',
                overflow: 'hidden',
                verticalAlign: 'middle',
                transition: 'background 0.12s ease'
            }}
        >
            {loading ? (
                <span
                    style={{
                        width: `${MESSAGE_CARD_THUMBNAIL_SIZE}px`,
                        height: `${MESSAGE_CARD_THUMBNAIL_SIZE}px`,
                        borderRadius: '10px',
                        flexShrink: 0,
                        background: theme.skeletonColor,
                        display: 'block'
                    }}
                />
            ) : hasMedia ? (
                <InlineMessageCardMedia
                    previewMessage={previewMessage}
                    size={referenceMediaSize}
                    theme={theme}
                />
            ) : null}
            <span
                style={{
                    minWidth: 0,
                    flex: '0 1 auto',
                    fontSize: '13px',
                    fontWeight: 600,
                    lineHeight: 1.3,
                    color: theme.titleColor,
                    whiteSpace: fileNeedsRoom ? 'normal' : 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    ...(fileNeedsRoom
                        ? {
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical'
                          }
                        : {})
                }}
            >
                {title}
            </span>
        </a>
    );
};

const imageFallbackBodyStyle = {
    border: '1px solid rgba(0, 0, 0, 0.12)',
    borderRadius: '8px',
    padding: '12px',
    background: 'rgba(0, 0, 0, 0.03)'
};

const ImageFallbackBody = ({ label }) => (
    <>
        <span style={{ display: 'block', fontWeight: 600, marginBottom: '4px' }}>{label || lnT('common:openImage')}</span>
        <span style={{ display: 'block', fontSize: '12px', opacity: 0.72, wordBreak: 'break-all' }}>{lnT('common:imagePreviewUnavailableFromThisSourceOpen')}</span>
    </>
);

const ImageFallbackLink = ({ href, label, style }) => (
    <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        style={{
            display: 'block',
            margin: '8px 0',
            color: style?.color || '#1f6fd6',
            textDecoration: 'none',
            maxWidth: '100%'
        }}
    >
        <span
            style={{
                ...imageFallbackBodyStyle,
                display: 'block'
            }}
        >
            <ImageFallbackBody label={label} />
        </span>
    </a>
);

const InlineImageLink = ({ href, label, onImageClick }) => {
    const {t: lnT} = useTranslation();

    const [hasError, setHasError] = React.useState(false);

    if (!href) {
        return null;
    }

    if (hasError) {
        return <ImageFallbackLink href={href} label={label} />;
    }

    const handleImageClick = event => {
        if (typeof onImageClick !== 'function') {
            return;
        }

        event.preventDefault();
        event.stopPropagation();
        onImageClick({
            src: href,
            alt: label || lnT('messages:embeddedImage')
        });
    };

    return (
        <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            onClick={handleImageClick}
            style={{ display: 'block', margin: '8px 0' }}
        >
            <img
                src={href}
                alt={label || lnT('messages:embeddedImage')}
                onError={() => {
                    setHasError(true);
                }}
                style={{
                    maxWidth: '100%',
                    height: 'auto',
                    display: 'block',
                    borderRadius: '8px',
                    cursor: typeof onImageClick === 'function' ? 'zoom-in' : undefined
                }}
            />
        </a>
    );
};

const safeAnchorComponent = (style, onImageClick, onMessageRefClick, surface) => {
    return ({ children, href, ...props }) => {

    const validProps = {};
    Object.keys(props).forEach(key => {
        if (key.includes(':') || key === 'href') {
            return;
        }
        const value = props[key];
        if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
            validProps[key] = value;
        }
    });

    const safeHref = href && typeof href === 'string' ? href : '#';
    const textChildren = React.Children.toArray(children)
        .filter(child => typeof child === 'string')
        .join('')
        .trim();

    if (isMessageRefHref(safeHref)) {
        const messageRefId = extractMessageRefId(safeHref);
        return (
            <InlineMessageCard
                id={messageRefId}
                label={textChildren}
                surface={surface}
                onMessageRefClick={onMessageRefClick}
            />
        );
    }

    const shouldRenderAsImage = isImageLikeHref(safeHref) || IMAGE_LINK_LABEL_REGEX.test(textChildren);

    if (shouldRenderAsImage) {
        return <InlineImageLink href={safeHref} label={textChildren} onImageClick={onImageClick} />;
    }

    const shouldRenderAsVideo = isVideoLikeHref(safeHref) || VIDEO_LINK_LABEL_REGEX.test(textChildren);

    if (shouldRenderAsVideo) {
        return <InlineVideoLink href={safeHref} label={textChildren} style={style} surface={surface} />;
    }

    return (
        <a {...validProps} href={safeHref} target="_blank" rel="noopener noreferrer" style={style}>
            {React.Children.map(children, child => typeof child === 'string' ? decodeDisplayText(child) : child)}
        </a>
    );
    };
};

const headingStyle = (tag, colors) => ({
    fontSize: `${COMPACT_MARKDOWN_TOKENS.headings[tag].fontSize}px`,
    lineHeight: String(COMPACT_MARKDOWN_TOKENS.headings[tag].lineHeight),
    fontWeight: COMPACT_MARKDOWN_TOKENS.headings[tag].fontWeight,
    margin: COMPACT_MARKDOWN_TOKENS.headings[tag].margin,
    color: tag === 'h6' ? colors.mutedHeadingColor : colors.headingColor
});

const markSearchHighlightTraversable = Component => {
    Component.searchHighlightTraverseChildren = true;
    return Component;
};

const startsWithCheckbox = child => {
    if (!React.isValidElement(child)) return false;
    if (child.props?.type === 'checkbox') return true;
    if (child.type === 'p' || child.type === 'li' || typeof child.type === 'function') {
        return startsWithCheckbox(React.Children.toArray(child.props.children)[0]);
    }
    return false;
};

const createListComponent = (tag, style) => markSearchHighlightTraversable(({ children, ...props }) => {
    const items = React.Children.toArray(children);
    const isChecklist = items.length > 0 && items.every(startsWithCheckbox);
    return React.createElement(tag, {
        ...props,
        style: { ...style, ...(isChecklist ? { paddingLeft: 0, listStyleType: 'none' } : {}) }
    }, children);
});

const createParagraphComponent = style => markSearchHighlightTraversable(({ children, ...props }) => {
    const childArray = React.Children.toArray(children);
    const hasTableStructuralChild = childArray.some(child => {
        if (!React.isValidElement(child)) {
            return false;
        }

        return ['thead', 'tbody', 'tfoot', 'tr', 'th', 'td', 'br'].includes(child.type);
    });

    if (hasTableStructuralChild) {
        return <>{children}</>;
    }

    const hasBlockChild = childArray.some(child => {
        if (!React.isValidElement(child)) {
            return false;
        }

        return ['ul', 'ol', 'pre', 'blockquote', 'div', 'table', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6'].includes(child.type);
    });

    const Element = hasBlockChild ? 'div' : 'p';
    return (
        <Element {...props} style={startsWithCheckbox(childArray[0]) ? { ...style, margin: 0 } : style}>
            {children}
        </Element>
    );
});

const createListItemComponent = style => markSearchHighlightTraversable(({ children, ...props }) => {
    const childArray = React.Children.toArray(children);
    const hasCheckboxChild = childArray.some(child => {
        if (!React.isValidElement(child)) {
            return false;
        }

        // Markdown inputs use a custom component, so their element type is
        // not the native 'input' string at this point.
        return startsWithCheckbox(child);
    });

    return (
        <li
            {...props}
            style={{
                ...style,
                listStyleType: hasCheckboxChild ? 'none' : undefined,
                display: hasCheckboxChild ? 'flex' : style.display,
                alignItems: hasCheckboxChild ? 'flex-start' : undefined,
                gap: hasCheckboxChild ? 0 : undefined,
                marginLeft: hasCheckboxChild ? 0 : undefined
            }}
        >
            {children}
        </li>
    );
});

const safeImageComponent = (style, onImageClick, surface) => ({ src, alt, title, fallbackHref, ...props }) => {
    const safeSrc = typeof src === 'string' ? src : '';
    const [hasError, setHasError] = React.useState(false);

    if (isDirectMarkdownVideo(safeSrc)) {
        return <InlineVideoLink href={safeSrc} fallbackHref={fallbackHref} label={alt || title} surface={surface} />;
    }

    if (!safeSrc) {
        return null;
    }

    if (hasError) {
        return (
            <span
                style={{
                    ...style,
                    ...imageFallbackBodyStyle,
                    height: 'auto',
                    display: 'block'
                }}
            >
                <ImageFallbackBody label={alt || title || lnT('common:openImage')} />
            </span>
        );
    }

    return (
        <img
            {...props}
            src={safeSrc}
            alt={alt || ''}
            title={title}
            onClick={event => {
                if (typeof onImageClick === 'function') {
                    event.preventDefault();
                    event.stopPropagation();
                    onImageClick({
                        src: safeSrc,
                        alt: alt || title || lnT('messages:embeddedImage')
                    });
                    return;
                }

                if (typeof props.onClick === 'function') {
                    props.onClick(event);
                }
            }}
            onError={() => {
                setHasError(true);
            }}
            style={{
                ...style,
                cursor: typeof onImageClick === 'function' ? 'zoom-in' : style?.cursor
            }}
        />
    );
};

const createTableComponent = (wrapperStyle, tableStyle) => ({ children, ...props }) => (
    <div style={wrapperStyle}>
        <table {...props} style={tableStyle}>
            {children}
        </table>
    </div>
);

const MarkdownInput = styled.input`
    &[type='checkbox'] {
        appearance: none;
        -webkit-appearance: none;
        box-sizing: border-box;
        width: 15px;
        height: 15px;
        margin: 2px 8px 0 0;
        border: 1.5px solid ${props => props.$dark ? 'rgba(255, 255, 255, 0.48)' : 'rgba(35, 39, 42, 0.45)'};
        border-radius: 4px;
        background-color: ${props => props.$dark ? 'rgba(255, 255, 255, 0.04)' : 'rgba(35, 39, 42, 0.03)'};
        background-position: center;
        background-repeat: no-repeat;
        background-size: 11px 11px;
        vertical-align: -2px;
        opacity: 1;
        transition: border-color 120ms ease, background-color 120ms ease;
    }

    &[type='checkbox']:checked {
        border-color: ${props => props.$dark ? '#63cbb9' : '#168574'};
        background-color: ${props => props.$dark ? '#63cbb9' : '#168574'};
        background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 12 12'%3E%3Cpath d='m2 6 2.5 2.5L10 3' fill='none' stroke='${props => props.$dark ? '%23152e29' : '%23fff'}' stroke-width='1.8' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
    }

    &[type='checkbox']:enabled:hover {
        border-color: ${props => props.$dark ? '#63cbb9' : '#168574'};
    }

    &[type='checkbox']:focus-visible {
        outline: 2px solid ${props => props.$dark ? '#63cbb9' : '#168574'};
        outline-offset: 3px;
    }

    @media (prefers-reduced-motion: reduce) {
        transition: none;
    }
`;

const createInputComponent = ({ onTaskCheckboxToggle, surface = 'light' } = {}) => {
    let taskIndex = 0;

    return ({ type, checked, disabled, style, onClick, onChange, ...props }) => {
        const isCheckbox = type === 'checkbox';
        const currentTaskIndex = isCheckbox ? taskIndex++ : null;
        const isInteractiveTaskCheckbox =
            isCheckbox && typeof onTaskCheckboxToggle === 'function' && currentTaskIndex !== null;

        return (
            <MarkdownInput
                {...props}
                $dark={surface !== 'light'}
                type={type}
                checked={isCheckbox ? Boolean(checked) : checked}
                disabled={isInteractiveTaskCheckbox ? false : (disabled ?? true)}
                readOnly={isCheckbox && !isInteractiveTaskCheckbox}
                onClick={event => {
                    event.stopPropagation();
                    if (typeof onClick === 'function') {
                        onClick(event);
                    }
                }}
                onChange={event => {
                    if (typeof onChange === 'function') {
                        onChange(event);
                    }

                    if (!isInteractiveTaskCheckbox) {
                        return;
                    }

                    onTaskCheckboxToggle({
                        taskIndex: currentTaskIndex,
                        checked: Boolean(event?.target?.checked)
                    });
                }}
                style={{
                    flexShrink: 0,
                    cursor: isInteractiveTaskCheckbox ? 'pointer' : 'default',
                    ...style
                }}
            />
        );
    };
};

export const createMarkdownToJsxOptions = ({
    surface = 'light',
    linkColor,
    textColor,
    quoteColor,
    onImageClick,
    onTaskCheckboxToggle,
    onMessageRefClick,
    hideListMarkers = false,
    bodyFontSizePx = COMPACT_MARKDOWN_TOKENS.body.fontSize,
    bodyLineHeight = COMPACT_MARKDOWN_TOKENS.body.lineHeight,
    paragraphMarginBottomPx = COMPACT_MARKDOWN_TOKENS.body.paragraphMarginBottom,
    // Highlight-quote tint: pass the highlight's palette color name ('yellow' |
    // 'green' | 'blue' | 'pink') to tint this message's blockquote to match.
    highlightColor
} = {}) => {
    const colors = getSurfaceColors({ surface, linkColor, textColor, quoteColor });
    const highlightTheme = highlightColor ? getHighlightColorTheme(highlightColor) : null;
    const resolvedBodyFontSize = Number.isFinite(bodyFontSizePx) ? bodyFontSizePx : COMPACT_MARKDOWN_TOKENS.body.fontSize;
    const resolvedBodyLineHeight = bodyLineHeight ?? COMPACT_MARKDOWN_TOKENS.body.lineHeight;
    const resolvedParagraphMarginBottom = Number.isFinite(paragraphMarginBottomPx)
        ? paragraphMarginBottomPx
        : COMPACT_MARKDOWN_TOKENS.body.paragraphMarginBottom;
    const bodyFontSize = `${resolvedBodyFontSize}px`;
    const bodyLineHeightValue = String(resolvedBodyLineHeight);
    const paragraphStyle = {
        margin: `0 0 ${resolvedParagraphMarginBottom}px 0`,
        fontSize: bodyFontSize,
        lineHeight: bodyLineHeightValue,
        color: colors.textColor
    };

    const mediaOptions = createMarkdownMediaOptions();
    const decodeTextChild = child => Array.isArray(child)
        ? child.map(decodeTextChild)
        : (typeof child === 'string' ? decodeDisplayText(child) : child);
    return {
        ...mediaOptions,
        // Notes/messages are markdown surfaces. Raw HTML here can inject page-level
        // styles (for example saved webpage <style> tags), so keep it escaped.
        disableParsingRawHTML: true,
        // Decode rendered text nodes after parsing, keeping href/src and code intact.
        createElement: (type, props, ...children) => mediaOptions.createElement(
            type,
            props,
            ...children.map(child => type !== 'code' && type !== 'pre'
                ? decodeTextChild(child)
                : child)
        ),
        overrides: {
            p: {
                component: createParagraphComponent(paragraphStyle)
            },
            strong: {
                props: {
                    style: {
                        fontWeight: 'bold',
                        fontSize: bodyFontSize,
                        lineHeight: bodyLineHeightValue,
                        color: colors.textColor
                    }
                }
            },
            em: {
                props: {
                    style: {
                        fontStyle: 'italic',
                        fontSize: bodyFontSize,
                        lineHeight: bodyLineHeightValue,
                        color: colors.textColor
                    }
                }
            },
            a: {
                component: safeAnchorComponent({
                    color: colors.linkColor,
                    textDecoration: 'underline',
                    wordBreak: 'break-all',
                    overflowWrap: 'break-word',
                    maxWidth: '100%',
                    display: 'inline-block',
                    fontSize: bodyFontSize,
                    lineHeight: bodyLineHeight
                }, onImageClick, onMessageRefClick, surface)
            },
            h1: { props: { style: headingStyle('h1', colors) } },
            h2: { props: { style: headingStyle('h2', colors) } },
            h3: { props: { style: headingStyle('h3', colors) } },
            h4: { props: { style: headingStyle('h4', colors) } },
            h5: { props: { style: headingStyle('h5', colors) } },
            h6: { props: { style: headingStyle('h6', colors) } },
            ul: {
                component: createListComponent('ul', {
                    margin: COMPACT_MARKDOWN_TOKENS.list.margin,
                    paddingLeft: hideListMarkers ? 0 : COMPACT_MARKDOWN_TOKENS.list.paddingLeft,
                    listStyleType: hideListMarkers ? 'none' : 'disc',
                    color: colors.textColor
                })
            },
            ol: {
                component: createListComponent('ol', {
                    margin: COMPACT_MARKDOWN_TOKENS.list.margin,
                    paddingLeft: hideListMarkers ? 0 : COMPACT_MARKDOWN_TOKENS.list.paddingLeft,
                    listStyleType: hideListMarkers ? 'none' : 'decimal',
                    color: colors.textColor
                })
            },
            li: {
                component: createListItemComponent({
                    margin: COMPACT_MARKDOWN_TOKENS.list.itemMargin,
                    fontSize: bodyFontSize,
                    lineHeight: bodyLineHeightValue,
                    color: colors.textColor,
                    display: 'list-item',
                    listStyleType: hideListMarkers ? 'none' : undefined
                })
            },
            blockquote: {
                props: {
                    style: {
                        borderLeft: `${COMPACT_MARKDOWN_TOKENS.quote.borderWidth}px solid ${
                            highlightTheme ? highlightTheme.border : colors.quoteBorderColor
                        }`,
                        paddingLeft: `${COMPACT_MARKDOWN_TOKENS.quote.paddingLeft}px`,
                        margin: COMPACT_MARKDOWN_TOKENS.quote.margin,
                        fontStyle: 'italic',
                        color: colors.quoteColor,
                        ...(highlightTheme ? { backgroundColor: highlightTheme.background } : {})
                    }
                }
            },
            code: {
                props: {
                    style: {
                        backgroundColor: colors.codeBackground,
                        padding: COMPACT_MARKDOWN_TOKENS.inlineCode.padding,
                        borderRadius: `${COMPACT_MARKDOWN_TOKENS.inlineCode.borderRadius}px`,
                        fontFamily: COMPACT_MARKDOWN_TOKENS.inlineCode.fontFamily,
                        fontSize: bodyFontSize,
                        lineHeight: bodyLineHeightValue,
                        color: colors.textColor
                    }
                }
            },
            pre: {
                props: {
                    style: {
                        backgroundColor: colors.preBackground,
                        padding: `${COMPACT_MARKDOWN_TOKENS.codeBlock.padding}px`,
                        borderRadius: `${COMPACT_MARKDOWN_TOKENS.codeBlock.borderRadius}px`,
                        overflow: 'auto',
                        margin: COMPACT_MARKDOWN_TOKENS.list.margin,
                        fontSize: bodyFontSize,
                        lineHeight: bodyLineHeightValue,
                        fontFamily: COMPACT_MARKDOWN_TOKENS.inlineCode.fontFamily,
                        color: colors.textColor
                    }
                }
            },
            img: {
                component: safeImageComponent({
                    maxWidth: '100%',
                    height: 'auto',
                    display: 'block',
                    margin: '8px 0',
                    borderRadius: '8px'
                }, onImageClick, surface)
            },
            table: {
                component: createTableComponent(
                    {
                        overflowX: 'auto',
                        margin: COMPACT_MARKDOWN_TOKENS.table.wrapperMargin,
                        maxWidth: '100%'
                    },
                    {
                        minWidth: `${COMPACT_MARKDOWN_TOKENS.table.minWidth}px`,
                        width: 'max-content',
                        borderCollapse: 'separate',
                        borderSpacing: 0,
                        border: `1px solid ${colors.hrColor}`,
                        borderRadius: `${COMPACT_MARKDOWN_TOKENS.table.borderRadius}px`,
                        background: surface === 'dark' ? 'rgba(255, 255, 255, 0.035)' : 'rgba(0, 0, 0, 0.02)',
                        overflow: 'hidden'
                    }
                )
            },
            thead: {
                props: {}
            },
            tbody: {
                props: {}
            },
            tfoot: {
                props: {}
            },
            tr: {
                props: {}
            },
            th: {
                props: {
                    style: {
                        borderRight: `1px solid ${colors.hrColor}`,
                        borderBottom: `1px solid ${colors.hrColor}`,
                        padding: COMPACT_MARKDOWN_TOKENS.table.cellPadding,
                        textAlign: 'left',
                        fontWeight: 600,
                        color: colors.headingColor,
                        background: surface === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)',
                        width: `${COMPACT_MARKDOWN_TOKENS.table.columnWidth}px`,
                        minWidth: `${COMPACT_MARKDOWN_TOKENS.table.columnWidth}px`,
                        maxWidth: `${COMPACT_MARKDOWN_TOKENS.table.columnWidth}px`,
                        verticalAlign: 'top'
                    }
                }
            },
            td: {
                props: {
                    style: {
                        borderRight: `1px solid ${colors.hrColor}`,
                        borderBottom: `1px solid ${colors.hrColor}`,
                        padding: COMPACT_MARKDOWN_TOKENS.table.cellPadding,
                        color: colors.textColor,
                        width: `${COMPACT_MARKDOWN_TOKENS.table.columnWidth}px`,
                        minWidth: `${COMPACT_MARKDOWN_TOKENS.table.columnWidth}px`,
                        maxWidth: `${COMPACT_MARKDOWN_TOKENS.table.columnWidth}px`,
                        verticalAlign: 'top'
                    }
                }
            },
            input: {
                component: createInputComponent({ onTaskCheckboxToggle, surface })
            },
            hr: {
                props: {
                    style: {
                        border: 'none',
                        borderTop: `1px solid ${colors.hrColor}`,
                        margin: COMPACT_MARKDOWN_TOKENS.hr.margin
                    }
                }
            }
        }
    };
};

const NBSP_MARKER_LINE_REGEX = /^\s*(?:&nbsp;|&#160;|\u00a0)\s*$/i;
const MARKDOWN_FENCE_REGEX = /^ {0,3}(`{3,}|~{3,})/;
const TASK_LIST_MARKER_LINE_REGEX = /^(\s*(?:[-+*]|\d+\.)\s+\[)([ xX])(\](?:\s.*)?)$/;
const BARE_TASK_MARKER_LINE_REGEX = /^( {0,3})\[([ xX]?)\][ \t]+(\S.*)$/;
const LINKED_MARKDOWN_IMAGE_REGEX = /\[!\[([^\]]*)\]\((https?:\/\/[^\s]+)\)\]\((https?:\/\/[^\s]+)\)/gi;
const CLIPPED_IMAGE_LINK_REGEX = /(^|[^!])\[(Image\s+\d+\s*:\s*Image)\]\((https?:\/\/[^\s]+)\)/gim;
const DUPLICATE_IMAGE_BANG_REGEX = /!+\[(?=[^\]]*\]\((https?:\/\/[^\s]+)\))/gi;
const RAW_HTML_TAG_REGEX = /<\/?[a-z][\w:-]*(?:\s[^<>]*)?>/gi;
const RAW_HTML_BLOB_SIGNAL_REGEX = /<(?:svg|path|style|div|section|article|button|img|a)\b/i;

const normalizeLineEndings = markdown => markdown.replace(/\r\n/g, '\n').replace(/\r/g, '\n');

export const normalizeLinkedMarkdownImages = (markdown = '') => {
    if (typeof markdown !== 'string' || markdown.length === 0) {
        return '';
    }

    return markdown.replace(LINKED_MARKDOWN_IMAGE_REGEX, (_, altText, imageUrl) => `![${altText}](${imageUrl})`);
};

export const normalizeClippedImageLinks = (markdown = '') => {
    if (typeof markdown !== 'string' || markdown.length === 0) {
        return '';
    }

    return markdown.replace(CLIPPED_IMAGE_LINK_REGEX, (_, prefix, label, url) => `${prefix}![${label}](${url})`);
};

export const normalizeDuplicateImageBangRuns = (markdown = '') => {
    if (typeof markdown !== 'string' || markdown.length === 0) {
        return '';
    }

    return markdown.replace(DUPLICATE_IMAGE_BANG_REGEX, '![');
};

// Matches a COMPLETE markdown link to a luckynote-message ref:
// [label](luckynote-message:<24-hex-id>). Deliberately requires the closing
// `)` so a link mid-stream (cut off before the id or the closing paren) is
// left untouched rather than corrupted.
const MESSAGE_REF_LINK_REGEX = /\[([^\]]*)\]\(luckynote-message:([a-fA-F0-9]{24})\)/g;

// The model can legitimately (or via a streaming glitch) cite the same saved
// item twice in one answer. Rendering used to dedupe this with a Set closed
// over per-render in safeAnchorComponent, but that Set persisted across the
// re-renders that happen while a reply streams in, so refs that were only
// ever seen once in the FINAL text got treated as duplicates because an
// earlier partial render pass had already "seen" that id. Doing the dedupe
// as a pure string transform instead — re-run on the current markdown string
// on every render — fixes that: it has no memory between renders, so it only
// ever judges the text that's actually in front of it right now.
// First occurrence of a given id stays a clickable link; any later
// occurrence of the SAME id is rewritten to plain text (label kept, link
// syntax dropped) so it doesn't render a second card.
export const dedupeMessageRefLinks = (markdown = '') => {
    if (typeof markdown !== 'string' || markdown.length === 0) {
        return typeof markdown === 'string' ? markdown : '';
    }

    const seenIds = new Set();

    return markdown.replace(MESSAGE_REF_LINK_REGEX, (match, label, id) => {
        if (seenIds.has(id)) {
            return label;
        }
        seenIds.add(id);
        return match;
    });
};

const getFenceStart = line => {
    const match = line.match(MARKDOWN_FENCE_REGEX);
    if (!match) {
        return null;
    }

    const fenceToken = match[1];
    return {
        char: fenceToken[0],
        length: fenceToken.length
    };
};

const isFenceEnd = (line, fenceState) => {
    if (!fenceState) {
        return false;
    }

    const trimmed = line.trimStart();
    if (!trimmed || trimmed[0] !== fenceState.char) {
        return false;
    }

    let markerLength = 0;
    while (markerLength < trimmed.length && trimmed[markerLength] === fenceState.char) {
        markerLength += 1;
    }

    if (markerLength < fenceState.length) {
        return false;
    }

    return trimmed.slice(markerLength).trim() === '';
};

const transformOutsideFenceLines = (lines, transformer) => {
    const output = [];
    const outsideFenceChunk = [];
    let fenceState = null;

    const flushChunk = () => {
        if (outsideFenceChunk.length === 0) {
            return;
        }

        output.push(...transformer(outsideFenceChunk));
        outsideFenceChunk.length = 0;
    };

    for (const line of lines) {
        if (fenceState) {
            output.push(line);
            if (isFenceEnd(line, fenceState)) {
                fenceState = null;
            }
            continue;
        }

        const nextFenceState = getFenceStart(line);
        if (nextFenceState) {
            flushChunk();
            output.push(line);
            fenceState = nextFenceState;
            continue;
        }

        outsideFenceChunk.push(line);
    }

    flushChunk();
    return output;
};

export const normalizeMarkdownForStorage = (markdown = '') => {
    if (typeof markdown !== 'string') {
        return '';
    }

    const normalizedMarkdown = normalizeLineEndings(
        normalizeDuplicateImageBangRuns(
            normalizeClippedImageLinks(normalizeLinkedMarkdownImages(markdown))
        )
    );
    const transformedLines = transformOutsideFenceLines(normalizedMarkdown.split('\n'), lines =>
        lines.map(line => {
            if (NBSP_MARKER_LINE_REGEX.test(line)) {
                return '';
            }
            return line.replace(BARE_TASK_MARKER_LINE_REGEX, (_, indent, state, text) =>
                `${indent}- [${state || ' '}] ${text}`
            );
        })
    );

    return transformedLines.join('\n');
};

export const shouldRenderMarkdownAsPlainText = (markdown = '') => {
    if (typeof markdown !== 'string') {
        return false;
    }

    const trimmedMarkdown = markdown.trim();
    if (!trimmedMarkdown.startsWith('<')) {
        return false;
    }

    const tagMatches = trimmedMarkdown.match(RAW_HTML_TAG_REGEX) || [];
    if (tagMatches.length < 12 || !RAW_HTML_BLOB_SIGNAL_REGEX.test(trimmedMarkdown)) {
        return false;
    }

    // Large clipped webpage fragments can send markdown-to-jsx into very deep
    // recursive parsing on desktop web. Render those as plain text instead.
    return trimmedMarkdown.length >= 2000 || tagMatches.length >= 32;
};

export const setMarkdownTaskCheckboxState = (markdown = '', taskIndex = -1, checked = false) => {
    if (typeof markdown !== 'string' || taskIndex < 0) {
        return typeof markdown === 'string' ? markdown : '';
    }

    let currentTaskIndex = 0;
    let didUpdate = false;
    const normalizedMarkdown = normalizeMarkdownForStorage(markdown);
    const transformedLines = transformOutsideFenceLines(normalizedMarkdown.split('\n'), lines =>
        lines.map(line => {
            const match = line.match(TASK_LIST_MARKER_LINE_REGEX);
            if (!match) {
                return line;
            }

            const resolvedTaskIndex = currentTaskIndex;
            currentTaskIndex += 1;

            if (resolvedTaskIndex !== taskIndex) {
                return line;
            }

            didUpdate = true;
            return `${match[1]}${checked ? 'x' : ' '}${match[3]}`;
        })
    );

    return didUpdate ? transformedLines.join('\n') : normalizedMarkdown;
};

const encodeBlankRunsForTiptap = lines => {
    const encodedLines = [];
    let blankRunLength = 0;

    const flushBlankRun = () => {
        if (blankRunLength === 0) {
            return;
        }

        if (blankRunLength < 3) {
            for (let i = 0; i < blankRunLength; i += 1) {
                encodedLines.push('');
            }
            blankRunLength = 0;
            return;
        }

        for (let i = 0; i < blankRunLength; i += 1) {
            const shouldUseNbspMarker = i % 2 === 1 && i < blankRunLength - 1;
            encodedLines.push(shouldUseNbspMarker ? '&nbsp;' : '');
        }

        blankRunLength = 0;
    };

    for (const line of lines) {
        if (line.trim() === '') {
            blankRunLength += 1;
            continue;
        }

        flushBlankRun();
        encodedLines.push(line);
    }

    flushBlankRun();
    return encodedLines;
};

export const prepareMarkdownForTiptap = (markdown = '') => {
    if (typeof markdown !== 'string') {
        return '';
    }

    const cleanMarkdown = normalizeMarkdownForStorage(markdown);
    const transformedLines = transformOutsideFenceLines(cleanMarkdown.split('\n'), encodeBlankRunsForTiptap);
    return transformedLines.join('\n');
};

// Shared tiptap markdown CSS for compact in-app surfaces.
// Supported surfaces:
// - `light`: Note editor and other light cards
// - `dark`: AI/assistant dark bubbles
// Phase 2 extension points: URL preview markdown and blog markdown renderers.
export const compactTiptapMarkdownCss = ({ surface = 'light', linkColor, textColor, quoteColor } = {}) => {
    const colors = getSurfaceColors({ surface, linkColor, textColor, quoteColor });

    return css`
        & .ln-note-editor {
            min-height: 276px;
            outline: none;
            white-space: pre-wrap;
            word-break: break-word;
            color: ${colors.textColor};
            font-size: ${COMPACT_MARKDOWN_TOKENS.body.fontSize}px;
            line-height: ${COMPACT_MARKDOWN_TOKENS.body.lineHeight};
        }

        & .ln-note-editor p,
        & .ln-note-editor strong,
        & .ln-note-editor em,
        & .ln-note-editor ul,
        & .ln-note-editor ol,
        & .ln-note-editor li,
        & .ln-note-editor blockquote,
        & .ln-note-editor code,
        & .ln-note-editor pre {
            font-size: ${COMPACT_MARKDOWN_TOKENS.body.fontSize}px;
            line-height: ${COMPACT_MARKDOWN_TOKENS.body.lineHeight};
        }

        & .ln-note-editor a {
            color: ${colors.linkColor};
            text-decoration: underline;
            word-break: break-all;
            overflow-wrap: break-word;
            max-width: 100%;
            display: inline-block;
        }

        & .ln-note-editor h1,
        & .ln-note-editor h2,
        & .ln-note-editor h3,
        & .ln-note-editor h4,
        & .ln-note-editor h5,
        & .ln-note-editor h6 {
            color: ${colors.headingColor};
        }

        & .ln-note-editor h1 {
            font-size: ${COMPACT_MARKDOWN_TOKENS.headings.h1.fontSize}px;
            line-height: ${COMPACT_MARKDOWN_TOKENS.headings.h1.lineHeight};
            font-weight: ${COMPACT_MARKDOWN_TOKENS.headings.h1.fontWeight};
            margin: ${COMPACT_MARKDOWN_TOKENS.headings.h1.margin};
        }

        & .ln-note-editor h2 {
            font-size: ${COMPACT_MARKDOWN_TOKENS.headings.h2.fontSize}px;
            line-height: ${COMPACT_MARKDOWN_TOKENS.headings.h2.lineHeight};
            font-weight: ${COMPACT_MARKDOWN_TOKENS.headings.h2.fontWeight};
            margin: ${COMPACT_MARKDOWN_TOKENS.headings.h2.margin};
        }

        & .ln-note-editor h3 {
            font-size: ${COMPACT_MARKDOWN_TOKENS.headings.h3.fontSize}px;
            line-height: ${COMPACT_MARKDOWN_TOKENS.headings.h3.lineHeight};
            font-weight: ${COMPACT_MARKDOWN_TOKENS.headings.h3.fontWeight};
            margin: ${COMPACT_MARKDOWN_TOKENS.headings.h3.margin};
        }

        & .ln-note-editor h4 {
            font-size: ${COMPACT_MARKDOWN_TOKENS.headings.h4.fontSize}px;
            line-height: ${COMPACT_MARKDOWN_TOKENS.headings.h4.lineHeight};
            font-weight: ${COMPACT_MARKDOWN_TOKENS.headings.h4.fontWeight};
            margin: ${COMPACT_MARKDOWN_TOKENS.headings.h4.margin};
        }

        & .ln-note-editor h5 {
            font-size: ${COMPACT_MARKDOWN_TOKENS.headings.h5.fontSize}px;
            line-height: ${COMPACT_MARKDOWN_TOKENS.headings.h5.lineHeight};
            font-weight: ${COMPACT_MARKDOWN_TOKENS.headings.h5.fontWeight};
            margin: ${COMPACT_MARKDOWN_TOKENS.headings.h5.margin};
        }

        & .ln-note-editor h6 {
            font-size: ${COMPACT_MARKDOWN_TOKENS.headings.h6.fontSize}px;
            line-height: ${COMPACT_MARKDOWN_TOKENS.headings.h6.lineHeight};
            font-weight: ${COMPACT_MARKDOWN_TOKENS.headings.h6.fontWeight};
            color: ${colors.mutedHeadingColor};
            margin: ${COMPACT_MARKDOWN_TOKENS.headings.h6.margin};
        }

        & .ln-note-editor p {
            margin: 0 0 ${COMPACT_MARKDOWN_TOKENS.body.paragraphMarginBottom}px 0;
        }

        & .ln-note-editor ul {
            list-style-type: disc;
            margin: ${COMPACT_MARKDOWN_TOKENS.list.margin};
            padding-left: ${COMPACT_MARKDOWN_TOKENS.list.paddingLeft};
        }

        & .ln-note-editor ol {
            list-style-type: decimal;
            margin: ${COMPACT_MARKDOWN_TOKENS.list.margin};
            padding-left: ${COMPACT_MARKDOWN_TOKENS.list.paddingLeft};
        }

        & .ln-note-editor li {
            margin: ${COMPACT_MARKDOWN_TOKENS.list.itemMargin};
            display: list-item;
        }

        & .ln-note-editor blockquote {
            border-left: ${COMPACT_MARKDOWN_TOKENS.quote.borderWidth}px solid ${colors.quoteBorderColor};
            padding-left: ${COMPACT_MARKDOWN_TOKENS.quote.paddingLeft}px;
            margin: ${COMPACT_MARKDOWN_TOKENS.quote.margin};
            font-style: italic;
            color: ${colors.quoteColor};
        }

        & .ln-note-editor code {
            background-color: ${colors.codeBackground};
            padding: ${COMPACT_MARKDOWN_TOKENS.inlineCode.padding};
            border-radius: ${COMPACT_MARKDOWN_TOKENS.inlineCode.borderRadius}px;
            font-family: ${COMPACT_MARKDOWN_TOKENS.inlineCode.fontFamily};
        }

        & .ln-note-editor pre {
            background-color: ${colors.preBackground};
            padding: ${COMPACT_MARKDOWN_TOKENS.codeBlock.padding}px;
            border-radius: ${COMPACT_MARKDOWN_TOKENS.codeBlock.borderRadius}px;
            overflow-x: auto;
        }

        & .ln-note-editor pre code {
            background: transparent;
            padding: 0;
        }

        & .ln-note-editor hr {
            border: none;
            border-top: 1px solid ${colors.hrColor};
            margin: ${COMPACT_MARKDOWN_TOKENS.hr.margin};
        }

        & .ln-note-editor img {
            max-width: 100%;
            height: auto;
            display: block;
            margin: 8px 0;
            border-radius: 8px;
        }

        & .ln-note-editor .tableWrapper {
            overflow-x: auto;
            margin: ${COMPACT_MARKDOWN_TOKENS.table.wrapperMargin};
            max-width: 100%;
        }

        & .ln-note-editor table {
            min-width: ${COMPACT_MARKDOWN_TOKENS.table.minWidth}px;
            width: max-content;
            border-collapse: separate;
            border-spacing: 0;
            border: 1px solid ${colors.hrColor};
            border-radius: ${COMPACT_MARKDOWN_TOKENS.table.borderRadius}px;
            background: ${surface === 'dark' ? 'rgba(255, 255, 255, 0.035)' : 'rgba(0, 0, 0, 0.02)'};
            overflow: hidden;
        }

        & .ln-note-editor th,
        & .ln-note-editor td {
            border-right: 1px solid ${colors.hrColor};
            border-bottom: 1px solid ${colors.hrColor};
            padding: ${COMPACT_MARKDOWN_TOKENS.table.cellPadding};
            vertical-align: top;
            width: ${COMPACT_MARKDOWN_TOKENS.table.columnWidth}px;
            min-width: ${COMPACT_MARKDOWN_TOKENS.table.columnWidth}px;
            max-width: ${COMPACT_MARKDOWN_TOKENS.table.columnWidth}px;
            text-align: left;
        }

        & .ln-note-editor th {
            color: ${colors.headingColor};
            font-weight: 600;
            background: ${surface === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)'};
        }

        & .ln-note-editor ul[data-type='taskList'] {
            list-style: none;
            padding-left: 0;
        }

        & .ln-note-editor ul[data-type='taskList'] li {
            display: flex;
            align-items: flex-start;
            gap: 8px;
        }

        & .ln-note-editor ul[data-type='taskList'] li > label {
            display: inline-flex;
            margin-top: 2px;
        }

        & .ln-note-editor > *:first-child {
            margin-top: 0;
        }

        & .ln-note-editor > *:last-child {
            margin-bottom: 0;
        }
    `;
};
